import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CaretLeft, Plus, Envelope, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuthGate } from "@/lib/hooks/useAuthGate";
import SessionExpiredState from "@/components/auth/SessionExpiredState";
import { getChildById } from "@/lib/dailyReportsDemo";
import {
  addEntry,
  deleteEntry,
  fetchReport,
  upsertReport,
} from "@/lib/dailyReports";
import { notifyDailyReport } from "@/lib/notifications";
import {
  ENTRY_META,
  ENTRY_TYPE_LIST,
  todayStr,
} from "@/lib/entryTypes";
import {
  deleteReportPhoto,
  listReportPhotos,
  reportPhotoSignedUrl,
  uploadReportPhoto,
} from "@/lib/storage";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useOrganizationConfig } from "@/context/OrganizationConfigContext";
import EntryCard from "@/components/reports/EntryCard";
import AddEntrySheet from "@/components/reports/AddEntrySheet";
import FileUploadCard from "@/components/upload/FileUploadCard";
import LearningObservationSheet from "@/components/teacher/LearningObservationSheet";
import {
  createLearningObservation,
  getTeacherLearningObservationSnapshot,
} from "@/lib/learningObservations";

export default function TeacherReportEditor() {
  const { childId } = useParams();
  const { status, profile } = useAuthGate();
  const { isModuleEnabled } = useOrganizationConfig();
  const today = useMemo(() => todayStr(), []);
  const learningEnabled = isModuleEnabled("learning");

  const [child, setChild] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeEntryType, setActiveEntryType] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [notifying, setNotifying] = useState(false);
  const [learningSnapshot, setLearningSnapshot] = useState(null);
  const [learningLoading, setLearningLoading] = useState(false);
  const [learningError, setLearningError] = useState("");
  const [learningReloadToken, setLearningReloadToken] = useState(0);
  const [learningOpen, setLearningOpen] = useState(false);
  const [learningSaving, setLearningSaving] = useState(false);
  const activeEntryTriggerRef = useRef(null);
  const learningTriggerRef = useRef(null);

  // Load child basic info (from demo fixtures in demo mode; from Supabase children table in live mode).
  useEffect(() => {
    if (!childId) return;
    const demoChild = getChildById(childId);
    if (demoChild) setChild(demoChild);
  }, [childId]);

  // Load today's report for this child (may be null). Gated on auth so a
  // missing/expired session resolves the loading state instead of hanging.
  useEffect(() => {
    if (status !== "ready") {
      if (status === "session_expired") setLoading(false);
      return;
    }
    if (!childId) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const r = await fetchReport(childId, today);
        setReport(r);
        if (r?.id && isSupabaseConfigured) {
          try {
            const ps = await listReportPhotos(r.id);
            setPhotos(ps);
          } catch {
            setPhotos([]);
          }
        } else {
          setPhotos([]);
        }
      } catch (err) {
        toast.error(err.message || "Could not load report");
      } finally {
        setLoading(false);
      }
    })();
  }, [status, childId, today]);

  // Learning observations are deliberately loaded separately from the daily
  // report. A teacher may record a genuine observation without manufacturing
  // an otherwise-empty daily report, while a report that exists can be linked
  // for explainable context when the observation is saved.
  useEffect(() => {
    if (status !== "ready" || !childId || !learningEnabled) {
      setLearningSnapshot(null);
      setLearningError("");
      setLearningLoading(false);
      return;
    }
    let cancelled = false;
    setLearningLoading(true);
    setLearningError("");
    getTeacherLearningObservationSnapshot({ childId, actorProfileId: profile?.id })
      .then((snapshot) => {
        if (!cancelled) setLearningSnapshot(snapshot);
      })
      .catch((error) => {
        if (!cancelled) setLearningError(error?.message || "Learning observations could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLearningLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [childId, learningEnabled, learningReloadToken, profile?.id, status]);

  const ensureReport = async () => {
    if (report) return report;
    const c = child;
    const created = await upsertReport({
      childId,
      classroomId: c?.classroom_id,
      reportDate: today,
      createdBy: profile.id,
      organizationId: profile.organization_id,
    });
    const full = { ...created, entries: [] };
    setReport(full);
    return full;
  };

  const onSubmitEntry = async (detail) => {
    const type = activeEntryType;
    setActiveEntryType(null);
    try {
      const r = await ensureReport();
      const entry = await addEntry({
        report: r,
        entryType: type,
        detail,
        createdBy: profile.id,
      });
      setReport((prev) => ({
        ...prev,
        entries: [...(prev?.entries || []), entry],
      }));
      toast.success(`${ENTRY_META[type].label} logged`);
    } catch (err) {
      toast.error(err.message || "Could not save entry");
    }
  };

  const onNotifyFamily = async () => {
    if (!report?.id || !child) return;
    setNotifying(true);
    try {
      let parentEmail = null;
      let parentProfileId = null;
      let orgName = null;
      if (isSupabaseConfigured) {
        const { data: c } = await supabase
          .from("children")
          .select(`
            id,
            family:families(id, primary_contact_profile_id,
              primary:profiles!families_primary_contact_profile_id_fkey(id, email)),
            organization:organizations(id, display_name, name)
          `)
          .eq("id", child.id)
          .maybeSingle();
        parentEmail = c?.family?.primary?.email || null;
        parentProfileId = c?.family?.primary_contact_profile_id || null;
        orgName = c?.organization?.display_name || c?.organization?.name || null;
      }
      if (!parentEmail || !parentProfileId) {
        toast.error("No primary contact email on file for this family.");
        return;
      }
      const res = await notifyDailyReport({
        report_id: report.id,
        child_id: child.id,
        child_first_name: child.first_name,
        child_last_name: child.last_name || null,
        report_date: today,
        parent_email: parentEmail,
        parent_profile_id: parentProfileId,
        organization_id: profile.organization_id,
        organization_name: orgName,
        summary_note: report.summary_note || null,
      });
      if (res.status === "sent") {
        toast.success("Family notified");
      } else if (res.status === "already_sent") {
        toast.success("Already sent earlier today");
      } else if (res.status === "skipped") {
        toast.message("Family has opted out of daily-report emails");
      } else {
        toast.error(res.reason || "Could not send");
      }
    } finally {
      setNotifying(false);
    }
  };

  const onDeleteEntry = async (entry) => {
    try {
      await deleteEntry({ report, entryId: entry.id });
      setReport((prev) => ({
        ...prev,
        entries: prev.entries.filter((e) => e.id !== entry.id),
      }));
      toast.success("Entry removed");
    } catch (err) {
      toast.error(err.message || "Could not delete entry");
    }
  };

  const onSubmitLearningObservation = async ({ focusId, note, observedAt }) => {
    setLearningSaving(true);
    try {
      const observation = await createLearningObservation({
        childId,
        focusId,
        observedAt,
        note,
        // Do not create a blank daily report just to record a learning moment.
        dailyReportId: report?.id || null,
        actorProfileId: profile?.id || null,
        actorName: profile?.full_name || null,
      });
      setLearningSnapshot((previous) => ({
        ...(previous || { status: "ready", childId, classroomId: child?.classroom_id || null, focuses: [] }),
        recentObservations: [observation, ...(previous?.recentObservations || [])].slice(0, 8),
      }));
      setLearningOpen(false);
      toast.success("Observation saved for administrator review");
    } catch (error) {
      toast.error(error?.message || "Could not save observation");
    } finally {
      setLearningSaving(false);
    }
  };

  const onUploadPhoto = async (file) => {
    const r = await ensureReport();
    const item = await uploadReportPhoto({
      reportId: r.id,
      file,
      uploadedBy: profile.id,
    });
    setPhotos((prev) => [...prev, item]);
  };

  const onRemovePhoto = async (item) => {
    await deleteReportPhoto({ id: item.id, storagePath: item.storage_path });
    setPhotos((prev) => prev.filter((p) => p.id !== item.id));
  };

  if (status === "session_expired") {
    return <SessionExpiredState scope="report" testId="report-editor-session-expired" />;
  }

  if (loading) {
    return (
      <div
        data-testid="report-editor-loading"
        className="flex items-center justify-center py-16"
      >
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  const entries = (report?.entries || [])
    .slice()
    .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));

  return (
    <div data-testid="teacher-report-editor" className="space-y-5">
      <Link
        to="/teacher/roster"
        data-testid="report-editor-back"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <CaretLeft size={16} weight="bold" />
        Back to roster
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Daily report · {today}
          </p>
          <h1
            data-testid="report-child-name"
            className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900"
          >
            {child?.first_name} {child?.last_name || ""}
          </h1>
          {child?.allergies && (
            <p
              data-testid="report-child-allergies"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-hover"
            >
              Allergy · {child.allergies}
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid="report-editor-notify-family"
          onClick={onNotifyFamily}
          disabled={notifying || !report?.id || (report?.entries?.length || 0) === 0}
          title={(report?.entries?.length || 0) === 0 ? "Add at least one entry first" : "Email today's report to the family"}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:opacity-50"
        >
          <Envelope size={12} weight="duotone" />
          {notifying ? "Sending…" : "Notify family"}
        </button>
      </header>

      {/* Quick-add chip grid */}
      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Quick log
          </p>
          {isModuleEnabled("food_program") ? (
            <Link
              to="/teacher/food-program"
              data-testid="report-editor-food-program-link"
              className="text-xs font-semibold text-primary-hover underline"
            >
              Recording the whole classroom meal? Use Food service
            </Link>
          ) : null}
        </div>
        <div
          data-testid="quick-log-chips"
          className="grid grid-cols-3 gap-2 sm:grid-cols-7"
        >
          {ENTRY_TYPE_LIST.map((type) => {
            const meta = ENTRY_META[type];
            const Icon = meta.icon;
            return (
              <button
                key={type}
                data-testid={`quick-log-${type}`}
                onClick={(event) => {
                  activeEntryTriggerRef.current = event.currentTarget;
                  setActiveEntryType(type);
                }}
                className="flex flex-col items-center gap-1.5 rounded-2xl border border-neutral-200 bg-white py-4 text-xs font-medium text-neutral-700 transition-all hover:-translate-y-[1px] hover:border-primary hover:bg-primary/5 hover:text-primary-hover active:scale-[0.97]"
              >
                <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${meta.bg}`}>
                  <Icon size={18} weight="duotone" className={meta.color} />
                </div>
                {meta.label}
              </button>
            );
          })}
          {learningEnabled ? (
            <button
              type="button"
              data-testid="quick-log-learning-observation"
              ref={learningTriggerRef}
              disabled={learningLoading || Boolean(learningError)}
              title={learningError || "Capture a learning observation for administrator review"}
              onClick={() => setLearningOpen(true)}
              className="flex flex-col items-center gap-1.5 rounded-2xl border border-primary/25 bg-primary/5 py-4 text-xs font-medium text-primary-hover transition-all hover:-translate-y-[1px] hover:border-primary hover:bg-primary/10 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15">
                <Sparkle size={18} weight="duotone" />
              </div>
              {learningLoading ? "Loading…" : "Observe"}
            </button>
          ) : null}
        </div>
        {learningEnabled && learningError ? (
          <p data-testid="learning-observation-load-error" className="mt-3 text-sm text-amber-800">
            Learning observations are unavailable right now. {" "}
            <button
              type="button"
              onClick={() => setLearningReloadToken((value) => value + 1)}
              className="font-semibold underline underline-offset-2"
            >
              Try again
            </button>
          </p>
        ) : null}
      </section>

      {/* Timeline */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Today's timeline
          </p>
          <p className="text-xs text-neutral-500">
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </p>
        </div>

        {entries.length === 0 ? (
          <div
            data-testid="report-empty"
            className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
              <Plus size={20} weight="bold" className="text-primary-hover" />
            </div>
            <div>
              <p className="font-heading text-base font-semibold text-neutral-900">
                Nothing logged yet
              </p>
              <p className="mt-1 text-sm text-neutral-500">
                Tap a chip above to add the first entry of the day.
              </p>
            </div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="report-entries">
            {entries.map((e) => (
              <li key={e.id}>
                <EntryCard entry={e} onDelete={onDeleteEntry} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <AddEntrySheet
        entryType={activeEntryType}
        open={Boolean(activeEntryType)}
        onOpenChange={(v) => !v && setActiveEntryType(null)}
        onSubmit={onSubmitEntry}
        restoreFocusRef={activeEntryTriggerRef}
      />

      {learningEnabled ? (
        <LearningObservationSheet
          open={learningOpen}
          childName={child?.first_name || "this child"}
          focuses={learningSnapshot?.focuses || []}
          saving={learningSaving}
          onOpenChange={setLearningOpen}
          onSubmit={onSubmitLearningObservation}
          restoreFocusRef={learningTriggerRef}
        />
      ) : null}

      {isSupabaseConfigured && (
        <FileUploadCard
          testId="report-photos"
          label="Photos"
          emptyLabel="No photos uploaded for today. Tap Upload to add one."
          accept="image/*"
          mode="image"
          items={photos}
          onUpload={onUploadPhoto}
          onRemove={onRemovePhoto}
          resolveSignedUrl={reportPhotoSignedUrl}
          maxSizeMb={8}
        />
      )}
    </div>
  );
}
